import { css } from '@emotion/react';
import { Add } from '@mui/icons-material';
import HandshakeOutlinedIcon from '@mui/icons-material/HandshakeOutlined';
import ThumbDownIconOutlined from '@mui/icons-material/ThumbDownOutlined';
import {
  Card,
  Icon,
  ListItem,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  Tooltip,
  Typography,
} from '@mui/material';
import { useAtomValue } from 'jotai/index';
import { FC, memo } from 'react';
import { MUIAvatar } from '../../../components/Avatar/MUIAvatar';
import { ICompanyDataModel } from '../../../data-models/company.data-model';
import { IListItemDataModel } from '../../../data-models/list-item.data-model';
import { ListType } from '../../../data-models/list.data-model';
import { useCurrentUser } from '../../../services/hooks/useCurrentUser';
import { usersByIdMapAtom } from '../../../services/state/AppConfigStateJ';
import { companyStateJ } from '../../../services/state/CompanyStateJ';
import { activeListStateJ } from '../../../services/state/ListsStateJ';
import { colors } from '../../../theme/colors';
import { getAllItemActionsByPriority, isItemHidden } from '../utils/list-utils';
import { TrackingSignifier } from './TrackingSignifier';

const EndIconsContainer = css`
  display: flex;
  align-items: center;
  gap: 0.5rem;
`;

const TextAndIcon = css`
  display: grid;
  align-items: center;
  justify-content: start;
  column-gap: 0.25rem;
  grid-template-columns: auto auto;
`;

const ListItemButtonCss = css`
  border-radius: 4px;
  max-height: 100%;
  max-width: 100%;
  &.Mui-selected {
    background: var(--mui-colors-primary-10);
  }
`;

const commonAvatarStyles = {
  border: '1px solid white',
};

const hiddenAvatarStyles = {
  filter: 'grayscale(0.9)',
  border: 'thin solid white',
  color: colors.neutral[60],
};

interface IListCardProps extends React.HTMLAttributes<HTMLDivElement> {
  item: IListItemDataModel;
  selected: boolean;
}

export const ListCard: FC<IListCardProps> = ({ item, onClick, selected }) => {
  const usersMap = useAtomValue(usersByIdMapAtom);
  const currentUser = useCurrentUser();
  const hiddenColor = 'text.secondary';
  const company = useAtomValue(companyStateJ(item.companyId));

  const activeList = useAtomValue(activeListStateJ);
  const isDiscoveryList = activeList?.type === ListType.DISCOVERY;

  if (!company || !activeList) {
    return null;
  }

  const isHidden = Boolean(item) && isDiscoveryList && isItemHidden(item, company, currentUser.id);
  const primaryText = company.name;
  const labelId = `checkbox-list-secondary-label-${company.name}`;
  const endIcon = getEndIcon(item, company, currentUser.id, isHidden);

  return (
    <ListItem disablePadding>
      <Card sx={{ width: '100%', mb: '.5rem' }} variant={'outlined'}>
        <ListItemButton
          css={ListItemButtonCss}
          selected={selected}
          onClick={onClick}
          sx={
            isHidden
              ? {
                  '&:hover': {
                    filter: 'brightness(1.05)',
                  },
                }
              : undefined
          }
          style={
            isHidden && !selected
              ? {
                  background:
                    'radial-gradient(100% 204.54% at 0% 0%, rgba(210, 213, 219, 0.44) 0%, rgba(210, 213, 219, 0.176) 100%)',
                }
              : undefined
          }
        >
          <ListItemAvatar sx={{ minWidth: '2.25rem' }}>
            <MUIAvatar
              nameParts={company.name.split(' ')}
              src={company.logoUrl ?? ''}
              style={{
                ...commonAvatarStyles,
                ...(isHidden ? hiddenAvatarStyles : {}),
              }}
            />
          </ListItemAvatar>
          <ListItemText
            id={labelId}
            primary={
              <div css={TextAndIcon}>
                {company.isConnected ? (
                  <HandshakeOutlinedIcon
                    titleAccess={'has previous deal'}
                    color={isHidden ? 'disabled' : 'secondary'}
                    sx={{
                      fontSize: '14px',
                    }}
                  />
                ) : null}
                <Typography variant='body2' noWrap title={primaryText} sx={{ minWidth: '1rem' }}>
                  {primaryText}
                </Typography>
              </div>
            }
            color={isHidden ? hiddenColor : ''}
            secondary={
              <Typography component='span' variant='caption' color={isHidden ? hiddenColor : ''}>
                {company.sector?.displayName}
              </Typography>
            }
          />
          <div css={EndIconsContainer} key={company.followers?.length}>
            {company.assignee && isDiscoveryList ? (
              <MUIAvatar
                src=''
                nameParts={usersMap.get(company.assignee)?.name?.split(/\s+/) ?? []}
                style={{
                  ...commonAvatarStyles,
                  ...(isHidden ? hiddenAvatarStyles : {}),
                }}
              />
            ) : (
              <Icon fontSize={'small'} />
            )}
            {endIcon}
          </div>
        </ListItemButton>
      </Card>
    </ListItem>
  );
};

function getEndIcon(
  item: IListItemDataModel,
  company: ICompanyDataModel,
  currentUserId: number,
  isHidden: boolean
) {
  const allItemActions = getAllItemActionsByPriority(item, company, currentUserId);

  let TooltipIcon = <Icon fontSize={'small'} />;
  let tooltipText = '';

  const strongestAction = allItemActions[0];

  if (strongestAction === 'pipeline') {
    tooltipText = 'Added to Pipeline';
    TooltipIcon = <Add color={isHidden ? 'disabled' : 'secondary'} fontSize={'small'} />;
  }

  if (strongestAction === 'hidden') {
    tooltipText = 'Passed';
    TooltipIcon = <ThumbDownIconOutlined color={isHidden ? 'disabled' : 'secondary'} fontSize={'small'} />;
  }

  if (strongestAction === 'followedByUser' || strongestAction === 'followedByOthers') {
    return <TrackingSignifier company={company} isHidden={isHidden} />;
  }

  return (
    <Tooltip title={tooltipText} placement='top'>
      <span>{TooltipIcon}</span>
    </Tooltip>
  );
}

function listCardsEqual(prevProps: IListCardProps, nextProps: IListCardProps) {
  return (
    prevProps.item.id === nextProps.item.id &&
    prevProps.selected === nextProps.selected &&
    prevProps.hidden === nextProps.hidden &&
    prevProps.onClick === nextProps.onClick
  );
}

export const MemoizedListCard = memo(ListCard, listCardsEqual);
